<script setup lang="ts">
import LightgalleryBase from './LightgalleryBase.vue'
import Icon from '~/components/global/Icon.vue'

const props = defineProps<{
  galleryId: string
  images: string[]
  virtualTourLink?: string
  preloadImages?: boolean
  hasNoImage?: boolean
}>()

const lightgalleryState = useLightgalleryState({
  galleryId: props.galleryId,
  allItems: props.images,
  itemsToShow: 3,
  dynamicItems: computed(() => {
    const items = []

    if (props.virtualTourLink) {
      items.push({
        iframe: true,
        src: props.virtualTourLink,
        thumb: '/images/360.svg',
      })
    }

    for (const image of props.images) {
      items.push({
        src: thumbnail(image, 'lightbox'),
        thumb: thumbnail(image, 'lightbox-thumb'),
      })
    }

    return items
  }),
})

const visibleItems = computed(() => {
  return lightgalleryState.visibleItems.value.map((image) => {
    if (lightgalleryState.visibleItems.value.length === 1) {
      return getImageSrcSizesData(image, {
        preload: props.preloadImages,
        defaultFormat: '1320x1320',
        sizes: `
          (max-width: 1199.99px) 95vw,
          (max-width: 1399.99px) 1100w,
          1320px
        `,
        srcsetToGenerate: [
          ['1100x1100', '1100w'],
          ['2200x2200', '2200w'],

          ['1320x1320', '1320w'],
          ['2640x2640', '2640w'],
        ],
      })
    }

    return getImageSrcSizesData(image, {
      preload: props.preloadImages,
      defaultFormat: '900x1800',
      sizes: `
        (max-width: 575.99px) 95vw,
        (max-width: 1199.99px) 66vw,
        (max-width: 1399.99px) 750px,
        900px
      `,
    })
  })
})
</script>

<template>
  <LightgalleryBase
    :lightgallery-state="lightgalleryState"
    :class="[
      'c-image-gallery',
      lightgalleryState.visibleItems.value.length === 1 &&
        'c-image-gallery--single',
      lightgalleryState.visibleItems.value.length === 2 &&
        'c-image-gallery--double',
    ]"
  >
    <IButton
      v-if="virtualTourLink"
      color="primary"
      class="c-image-gallery__360"
      @click="lightgalleryState.openGalleryAtIndex(0)"
    >
      <Icon name="360" />
    </IButton>

    <IButton
      v-if="!hasNoImage"
      color="primary"
      class="c-image-gallery__fullscreen"
      @click="lightgalleryState.openGalleryAtIndex(virtualTourLink ? 1 : 0)"
    >
      <Icon name="fullscreen" />
    </IButton>

    <button
      v-for="(item, index) in visibleItems"
      :key="index"
      :class="[
        'c-image-gallery__item',
        `c-image-gallery__item--index-${index + 1}`,
      ]"
      @click="
        lightgalleryState.openGalleryAtIndex(index + (virtualTourLink ? 1 : 0))
      "
    >
      <img
        class="sc-image"
        :src="item.src"
        :sizes="item.sizes"
        :srcset="item.srcset"
      />

      <div
        v-if="
          index + 1 === lightgalleryState.itemsToShow &&
          lightgalleryState.moreItemsCount.value > 0
        "
        class="sc-overlay"
      >
        +{{ lightgalleryState.moreItemsCount.value + 1 }}
      </div>
    </button>
  </LightgalleryBase>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0
      hsla(
        var(--color-primaryDark-h),
        var(--color-primaryDark-s),
        var(--color-primaryDark-l),
        0.7
      );
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px
      hsla(
        var(--color-primaryDark-h),
        var(--color-primaryDark-s),
        var(--color-primaryDark-l),
        0
      );
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0
      hsla(
        var(--color-primaryDark-h),
        var(--color-primaryDark-s),
        var(--color-primaryDark-l),
        0
      );
  }
}

.c-image-gallery {
  display: grid;
  gap: 20px;
  grid-template:
    '_1 _2' 1fr
    '_1 _3' 1fr
    / 2.25fr 1fr;
  aspect-ratio: 22 / 9;
  position: relative;

  &--double {
    grid-template:
      '_1 _2' 1fr
      / 1fr 1fr;
    aspect-ratio: 44 / 13;
  }

  &--single {
    grid-template:
      '_1' 1fr
      / 1fr;
    aspect-ratio: 2 / 1;
  }

  @include breakpoint-down('md') {
    gap: 10px;
  }

  @include breakpoint-down('xs') {
    gap: 6px;
    grid-template:
      '_1 _1' 2.05fr
      '_2 _3' 1fr
      / 1fr 1fr;
    aspect-ratio: 67 / 63;

    &--double {
      grid-template:
        '_1' 1fr
        '_2' 1fr
        / 1fr;
      aspect-ratio: 22 / 26;
    }

    &--single {
      grid-template:
        '_1' 1fr
        / 1fr;
      aspect-ratio: 5 / 3;
    }
  }

  &__lightgallery {
    position: absolute;
  }

  &__360.button {
    --button--font-size: 34px;
    --button--padding-top: 16px;
    --button--padding-bottom: 16px;
    --button--padding-left: 16px;
    --button--padding-right: 16px;
    --button--background: hsla(
      var(--color-primaryDark-h),
      var(--color-primaryDark-s),
      var(--color-primaryDark-l),
      0.9
    );

    grid-area: _1;
    position: absolute;
    bottom: 20px;
    left: 20px;
    z-index: 2;
    min-height: 0;

    box-shadow: 0 0 0 0
      hsla(
        var(--color-primaryDark-h),
        var(--color-primaryDark-s),
        var(--color-primaryDark-l),
        1
      );
    transform: scale(1);
    animation: pulse 2s infinite;

    @include breakpoint-down('md') {
      --button--font-size: 24px;
      --button--padding-top: 10px;
      --button--padding-bottom: 10px;
      --button--padding-left: 10px;
      --button--padding-right: 10px;
      bottom: 10px;
      left: 10px;
    }
  }

  &__fullscreen.button {
    --button--font-size: 28px;
    --button--padding-top: 10px;
    --button--padding-bottom: 10px;
    --button--padding-left: 10px;
    --button--padding-right: 10px;
    --button--background: hsla(
      var(--color-dark-h),
      var(--color-dark-s),
      var(--color-dark-l),
      0.2
    );

    grid-area: _1;
    position: absolute;
    bottom: 20px;
    right: 20px;
    z-index: 2;
    min-height: 0;

    @include breakpoint-down('md') {
      --button--font-size: 20px;
      bottom: 10px;
      right: 10px;
    }
  }

  &__item {
    min-height: 0;
    min-width: 0;
    position: relative;
    border: none;
    padding: 0;

    @for $i from 1 through 3 {
      &--index-#{$i} {
        grid-area: _#{$i};
      }
    }

    .sc-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .sc-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(--font-family-secondary-base);
      font-size: 72px;
      color: var(--color-white);
      background-color: hsla(
        var(--color-primary-h),
        var(--color-primary-s),
        var(--color-primary-l),
        0.75
      );

      @include breakpoint-down('md') {
        font-size: 40px;
      }
    }
  }
}
</style>
